import React, { useMemo } from 'react';
import Card from '@common/components/card';
import { EStaticComponentTypes } from '@modules/learning/definitions';
import { QuestionIcon } from '@modules/performance/components/question-icon/question-icon';
import { PerformanceQuestionType } from '@modules/performance/definitions';
import { useTranslation } from 'react-i18next';
import { useStickyHeader } from './hooks/use-sticky-header';

import FileComponent from '@common/components/form/user-response-fields/components/file';
import Video from '@common/components/form/user-response-fields/wrappers/video';
import Text from '@common/components/form/user-response-fields/wrappers/text';
import ExternalVideoEmbed from '@common/components/form/user-response-fields/wrappers/external-video-embed';
import ScreenSection from '@common/components/form/user-response-fields/screen-section';
import Image from '@common/components/form/user-response-fields/wrappers/image';

type LearningComponent = {
  id: string;
  type: string;
  index: number;
  parameters?: Record<string, any>;
};

export const getPreviewComponent = (type: string) => {
  switch (type) {
    case EStaticComponentTypes.IMAGE:
      return Image;
    case EStaticComponentTypes.TEXT:
      return Text;
    case EStaticComponentTypes.EXTERNAL_VIDEO:
      return ExternalVideoEmbed;
    case EStaticComponentTypes.VIDEO:
      return Video;
    case EStaticComponentTypes.PDF:
      return FileComponent;
    default:
      return null;
  }
};

const ContentModule = ({ component }: { component: any }) => {
  const { t } = useTranslation();
  const { sentinelRef, stickyHeaderRef, isHeaderSticky, defaultTopOffset } = useStickyHeader();

  const components: LearningComponent[] = useMemo(() => {
    const settingsItems = component?.settings;
    const base = Array.isArray(settingsItems) ? settingsItems : [];
    return [...base].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  }, [component]);

  if (!components || components.length === 0) return null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div ref={sentinelRef} className="tw-h-0 tw-w-full tw-mb-[-16px]" />
      <div
        ref={stickyHeaderRef}
        style={{
          zIndex: 1,
          '--top-offset': `${defaultTopOffset ?? 203}px`,
        } as React.CSSProperties}
        className={`${
          isHeaderSticky
            ? '!tw-bg-white !tw-p-5 !tw-border-b tw-border-0 tw-border-solid !tw-border-gray-border' +
              ' tw-sticky tw-top-[var(--top-offset)] tw-transition-all tw-duration-200'
            : ''
        }`}
      >
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
          <QuestionIcon
            type={PerformanceQuestionType.CONTENT_MODULE}
            className="!tw-w-4 !tw-h-4"
          />
          <div className="tw-text-gray-textDark tw-text-xl tw-font-semibold">
            {t('performance:question_type_title_content_module')}
          </div>
        </div>
      </div>
      <Card key={component.id}>
        <Card.Content className="tw-flex tw-flex-col !tw-p-5 !tw-gap-5">
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-flex-col tw-gap-3">
              {components.map((comp) => {
                const Preview = getPreviewComponent(comp.type);
                if (!Preview) return null;
                return (
                  <ScreenSection key={comp.id} contentClassName="!tw-w-[50%]">
                    <div className="ScreenComponent" key={comp.id}>
                      <div key={comp.id} className="tw-w-full">
                        <Preview item={comp as any} />
                      </div>
                    </div>
                  </ScreenSection>
                );
              })}
            </div>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default ContentModule;
